import { useState } from "react";
import ReferencePicker from "../EmissionFactorBreakdown/ReferencePicker";
import { saveEnergyFactor } from "./energyApi";
import { energyPerUnit, factorDraftError, factorDraftToPayload, formatValue } from "./helpers";

/**
 * Adds an energy factor, or edits one: the KPI and the unit its readings are
 * converted into, the source and country it prices, and the fuel density and
 * calorific value whose product is the GJ per unit, each with the reference
 * it is quoted from. The draft is checked before it is sent, and the
 * backend's answer is shown in place when it refuses.
 *
 * @param {object} props
 * @param {import("./helpers").FactorDraft} props.initial
 * @param {import("./helpers").EnergyFactor[]} props.factors every factor, for the duplicate check
 * @param {import("./helpers").EnergyKpi[]} props.kpis
 * @param {import("./helpers").EnergySource[]} props.sources
 * @param {import("../EmissionFactorBreakdown/referencesApi").GhgReference[]} props.references
 * @param {() => void} props.onReferencesChanged
 * @param {(saved: import("./helpers").EnergyFactor) => void} props.onSaved
 * @param {() => void} props.onCancel
 */
export default function FactorForm({ initial, factors, kpis, sources, references, onReferencesChanged, onSaved, onCancel }) {
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const editing = initial.id != null;
  const kpi = kpis.find((candidate) => String(candidate.kpi_id) === draft.kpiId) || null;
  const unit = kpi?.units.find((candidate) => String(candidate.id) === draft.unitId) || null;
  const perUnit = energyPerUnit({ fuel_density: Number(draft.density) || null, calorific_value: Number(draft.calorific) || null });

  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const change = (key) => (e) => set(key, e.target.value);

  const changeKpi = (e) => {
    const { value } = e.target;
    const picked = kpis.find((candidate) => String(candidate.kpi_id) === value);
    setDraft((current) => {
      const keepsUnit = picked?.units.some((candidate) => String(candidate.id) === current.unitId);
      const onlyUnit = picked?.units.length === 1 ? String(picked.units[0].id) : "";
      return { ...current, kpiId: value, unitId: keepsUnit ? current.unitId : onlyUnit };
    });
  };

  const submit = async () => {
    const problem = factorDraftError(draft, factors, kpis);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    const res = await saveEnergyFactor(draft.id, factorDraftToPayload(draft));
    setSaving(false);
    if (res?.isSuccess && res.data?.data) onSaved(res.data.data);
    else setError(res?.data?.error || "Couldn't save the energy factor.");
  };

  const title = editing ? `Edit the ${kpi?.title || "energy"} factor` : "Add an energy factor";

  return (
    <section className="kgb-card gdb-form" aria-label={title}>
      <header className="gdb-form-head">
        <h2>{title}</h2>
        <p>Energy (GJ) = reading in the factor's unit × fuel density × calorific value.</p>
      </header>

      <div className="gdb-form-body">
        <div className="gdb-form-grid">
          <label className="gdb-field gdb-field--wide" htmlFor="enf-factor-kpi">
            <span>KPI *</span>
            <select id="enf-factor-kpi" value={draft.kpiId} onChange={changeKpi}>
              <option value="">Pick an energy KPI…</option>
              {kpis.map((candidate) => (
                <option key={candidate.kpi_id} value={String(candidate.kpi_id)}>
                  {candidate.title}
                </option>
              ))}
            </select>
          </label>

          <label className="gdb-field" htmlFor="enf-factor-unit">
            <span>Unit *</span>
            <select id="enf-factor-unit" value={draft.unitId} disabled={!kpi} onChange={change("unitId")}>
              <option value="">{kpi ? "Pick a unit…" : "Pick a KPI first"}</option>
              {(kpi?.units || []).map((candidate) => (
                <option key={candidate.id} value={String(candidate.id)}>
                  {candidate.unit}
                </option>
              ))}
            </select>
            <small>Readings in other units are converted to this one.</small>
          </label>

          <label className="gdb-field" htmlFor="enf-factor-source">
            <span>Energy source *</span>
            <select id="enf-factor-source" value={draft.sourceId} onChange={change("sourceId")}>
              <option value="">Pick a source…</option>
              {sources.map((source) => (
                <option key={source.id} value={String(source.id)}>
                  {source.name}
                </option>
              ))}
            </select>
          </label>

          <label className="gdb-field" htmlFor="enf-factor-country">
            <span>Country *</span>
            <input
              id="enf-factor-country"
              type="text"
              maxLength={3}
              value={draft.countryCode}
              placeholder="IN"
              onChange={change("countryCode")}
            />
            <small>Energy pricing reads IN factors today.</small>
          </label>

          <label className="gdb-field" htmlFor="enf-factor-fuel">
            <span>Fuel type</span>
            <input id="enf-factor-fuel" type="text" maxLength={100} value={draft.fuelType} placeholder="Diesel" onChange={change("fuelType")} />
          </label>

          <label className="gdb-field" htmlFor="enf-factor-density">
            <span>Fuel density *</span>
            <input
              id="enf-factor-density"
              type="number"
              min="0"
              step="any"
              value={draft.density}
              placeholder="0.832"
              onChange={change("density")}
            />
            <small>{unit ? `kg per ${unit.unit}; 1 when the unit is already a mass or energy.` : "kg per unit of the reading."}</small>
          </label>

          <label className="gdb-field" htmlFor="enf-factor-calorific">
            <span>Calorific value *</span>
            <input
              id="enf-factor-calorific"
              type="number"
              min="0"
              step="any"
              value={draft.calorific}
              placeholder="0.043"
              onChange={change("calorific")}
            />
            <small>GJ per kg, or GJ per unit when the density is 1.</small>
          </label>

          <div className="gdb-field enf-preview" aria-live="polite">
            <span>Energy factor</span>
            <strong>{perUnit == null ? "—" : `${formatValue(perUnit)} GJ / ${unit?.unit || "unit"}`}</strong>
          </div>

          <ReferencePicker
            id="enf-factor-density-reference"
            label="Where the fuel density comes from"
            value={draft.densityReferenceId}
            references={references}
            onChange={(referenceId) => set("densityReferenceId", referenceId)}
            onReferencesChanged={onReferencesChanged}
          />
          <ReferencePicker
            id="enf-factor-calorific-reference"
            label="Where the calorific value comes from"
            value={draft.calorificReferenceId}
            references={references}
            onChange={(referenceId) => set("calorificReferenceId", referenceId)}
            onReferencesChanged={onReferencesChanged}
          />
        </div>

        {error ? (
          <p className="gdb-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="gdb-form-actions">
          <button type="button" className="kgb-btn kgb-btn--primary" disabled={saving} onClick={submit}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add factor"}
          </button>
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}
